import * as React from 'react';
import { Link, withRouter, RouteComponentProps } from 'react-router-dom';
import { Button, Card, Col, Divider, Modal, notification, Row, Table, Alert } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';

import OrderStatesQuery from '../../components/Query/OrderStates';
import OrderStateDetail from './OrderStateDetail';
import { Title } from '../../components/Layout/components';
import NotFoundPage from '../NotFound/NotFound';

interface IOrderStatesTableProps extends RouteComponentProps {
  domain: string;
  orderStateId?: string;
}

interface IOrderStatesTableState {
}

class OrderStatesTable extends React.Component<IOrderStatesTableProps, IOrderStatesTableState> {

  constructor(props: IOrderStatesTableProps) {
    super(props);
  }

  public componentDidMount() {
    if ((this.props as any).onCollapse) {
      (this.props as any).onCollapse(false);
    }
  }

  public render() {
    return (
      <Row>
        <Col span={24}>
          <OrderStatesQuery
            domain={this.props.domain}
          >
            {({ externalOrderStates, deleteExternalOrderState, createExternalOrderState, updateExternalOrderState }: any) => {
              return this.renderData(
                externalOrderStates,
                updateExternalOrderState,
                createExternalOrderState,
                deleteExternalOrderState,
                externalOrderStates.refetch
              );
            }}
          </OrderStatesQuery>
        </Col>
      </Row>
    );
  }

  private renderData(
    orderStates: any,
    updateOrderState: Function,
    createOrderState: Function,
    deleteOrderState: Function,
    refetch: Function
  ) {
    if (orderStates && !orderStates.loading) {

      // formulář
      if (this.props && this.props.orderStateId) {
        const selectedOrderState = orderStates.externalOrderStates.find((d: GQL.IExternalOrderState) => d.id === this.props.orderStateId) || undefined;
        if (this.props.orderStateId !== 'new' && !selectedOrderState) {
          return <NotFoundPage domain={this.props.domain} />
        }
        return (
          <OrderStateDetail
            domain={this.props.domain}
            languages={orderStates.languages}
            orderState={orderStates.externalOrderStates.find((s: GQL.IExternalOrderState) => s.id === this.props.orderStateId) || undefined}
            onSave={a => this.upsert(a, createOrderState, updateOrderState, refetch)}
            onCancel={() => this.cancel()}
          />
        );
      }

      // kontrola výchozího stavu
      const startupState = orderStates.externalOrderStates.find((d: GQL.IExternalOrderState) => d.startup === true);

      return (
        <div>
          <Title>Order States</Title>
          {!startupState &&
            <Alert message="Startup order state not defined!" type="error" />}
          <Divider />
          <Link className="ant-btn" to={this.getLink('new')}>Create new order state</Link>
          <Table
            rowKey={(record: any) => record.id}
            dataSource={orderStates.externalOrderStates}
            defaultExpandAllRows={true}
            style={{ marginTop: '20px' }}
          >
            <Table.Column
              title="Title"
              dataIndex="title"
              key="title"
            />
            <Table.Column
              title="Priority"
              dataIndex="priority"
              key="priority"
              width={100}
              align="center"
            />
            <Table.Column
              title="Paid"
              dataIndex="forPaidOrder"
              key="forPaidOrder"
              width={100}
              align="center"
              render={(u, row: GQL.IExternalOrderState) => {
                return (
                  <span>
                    {row.forPaidOrder 
                      ? <CheckOutlined style={{ color: '#52c41a', fontSize: 20 }} />
                      : <CloseOutlined style={{ fontSize: 20 }} />}
                  </span>
                );
              }}
            />
            <Table.Column
              title="Startup"
              dataIndex="startup"
              key="startup"
              width={100}
              align="center"
              render={(u, row: GQL.IExternalOrderState) => {
                return (
                  <span>
                    {row.startup 
                      ? <CheckOutlined style={{ color: '#52c41a', fontSize: 20 }} />
                      : <CloseOutlined style={{ fontSize: 20 }} />}
                  </span>
                );
              }}
            />
            <Table.Column
              title="Actions"
              dataIndex="actions"
              key="actions"
              width={180}
              align="center"
              render={(u: any, row: any) => this.renderActions(u, row, deleteOrderState, refetch)}
            />
          </Table>
        </div>
      );
    }

    return (
      <div>
        <Divider />
        <Card loading={true} title="Loading order states ..." style={{ width: '100%' }} />
      </div>
    );
  }

  private renderActions(u: any, orderState: GQL.IExternalOrderState, deleteOrderState: Function, refetch: Function) {
    return (
      <div>
        <Link className="ant-btn" type={'dashed'} to={this.getLink(orderState.id)}>
          Edit
        </Link>
        <Divider type={'vertical'} />
        <Button danger={true} onClick={() => this.delete(orderState, deleteOrderState, refetch)}>
          Delete
        </Button>
      </div>
    );
  }

  private delete(orderState: GQL.IExternalOrderState, deleteOrderState: Function, refetch: Function) {
    Modal.confirm({
      title: `Delete ${orderState.title}?`,
      content: (
        <span>
          Are you sure you want to delete order state <strong>{orderState.title}</strong>?
        </span>
      ),
      onOk: () => {
        return deleteOrderState({
          variables: {
            id: orderState.id
          }
        }).then(() => refetch());
      }
    });
  }

  private upsert(
    orderState: GQL.IExternalOrderState,
    createOrderState: Function,
    updateOrderState: Function,
    refetch: Function
  ) {
    if (orderState != undefined) {
      const variables = {
        id: orderState.id,
        externalOrderState: {
          title: orderState.title,
          name: orderState.name,
          type: orderState.type || 'info',
          priority: orderState.priority,
          color: orderState.color,
          startup: orderState.startup || false,
          forPaidOrder: orderState.forPaidOrder,
          forSentOrder: orderState.forSentOrder || false,
          setOrderAsPaid: orderState.setOrderAsPaid || false,
        }
      };

      if (orderState.id) {
        return updateOrderState({ variables })
          .then(() => refetch())
          .then(() => this.cancel())
          .then(() =>
            notification['success']({
              message: 'Info',
              description: `Order state "${variables.externalOrderState.title}" has been updated.`
            })
          );
      }

      (variables.externalOrderState as any).domain = this.props.domain;

      createOrderState({ variables })
        .then(() => refetch())
        .then(() => this.cancel())
        .then(() =>
          notification['success']({
            message: 'Info',
            description: `Order state "${variables.externalOrderState.title}" has been created.`
          })
        );
    }
  }

  private getLink(recordId: string | null) {
    return `/${this.props.domain}/application/order-state${recordId ? `/${recordId}` : ''}`;
  }

  private cancel() {
    this.props.history.push(this.getLink(null));
  }
}

export default withRouter(OrderStatesTable);



// WEBPACK FOOTER //
// ./src/scenes/OrderStates/OrderStatesTable.tsx